/**
 * Shared demo sandbox — no separate frontend deploy required.
 *
 * Demo UI is ON when any of:
 * - REACT_APP_DEMO_MODE=true (local dev)
 * - Host is demo.mobeat.io (optional Amplify custom domain on the same app)
 * - User opened /auth/login?demo=1 or clicked "Try live demo" (sessionStorage for the tab)
 */
import { PATH_AUTH } from 'routes/paths';

const DEMO_SESSION_KEY = 'mobeat_demo_sandbox';
const DEFAULT_DEMO_HOSTS = ['demo.mobeat.io'];

function parseDemoHosts() {
  const raw = process.env.REACT_APP_DEMO_HOSTS?.trim();
  if (!raw) {
    return DEFAULT_DEMO_HOSTS;
  }
  return raw
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
}

function syncDemoFromUrl() {
  if (typeof window === 'undefined') {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  const demoParam = params.get('demo');
  if (demoParam === '1' || demoParam === 'true') {
    sessionStorage.setItem(DEMO_SESSION_KEY, '1');
  }
}

export function activateDemoSession() {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(DEMO_SESSION_KEY, '1');
  }
}

export function clearDemoSession() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(DEMO_SESSION_KEY);
  }
}

export function isDemoMode() {
  if (process.env.REACT_APP_DEMO_MODE === 'true' || process.env.REACT_APP_DEMO_MODE === '1') {
    return true;
  }
  if (typeof window === 'undefined') {
    return false;
  }

  syncDemoFromUrl();

  if (sessionStorage.getItem(DEMO_SESSION_KEY) === '1') {
    return true;
  }

  const host = window.location.hostname.toLowerCase();
  return parseDemoHosts().some((demoHost) => host === demoHost || host.endsWith(`.${demoHost}`));
}

/** Login path on the current origin (same-site demo entry). */
export function getDemoLoginPath() {
  return `${PATH_AUTH.login}?demo=1`;
}

export function getConfiguredDemoOrigin() {
  const configured = process.env.REACT_APP_DEMO_URL?.trim().replace(/\/$/, '');
  if (!configured) {
    return null;
  }
  try {
    return new URL(configured).origin;
  } catch {
    return null;
  }
}

/** True when REACT_APP_DEMO_URL points to a different host than the current page. */
export function isExternalDemoLogin() {
  if (typeof window === 'undefined') {
    return Boolean(getConfiguredDemoOrigin());
  }
  const configuredOrigin = getConfiguredDemoOrigin();
  return Boolean(configuredOrigin && configuredOrigin !== window.location.origin);
}

export function getDemoLoginUrl() {
  const configured = process.env.REACT_APP_DEMO_URL?.trim().replace(/\/$/, '');
  if (configured) {
    return `${configured}/auth/login?demo=1`;
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${getDemoLoginPath()}`;
  }
  return getDemoLoginPath();
}

/** Show CTA on marketing pages when not already in demo mode. */
export function isLiveDemoEntryEnabled() {
  return !isDemoMode();
}

/** Documented shared tenant — demo logins use this customerId in Cognito (see server seed). */
export const SHARED_DEMO_CUSTOMER_ID = 'cafe0001-babe-4001-8001-000000000001';
